// src/features/release-notes/components/ReleaseNoteCard.tsx

import React from 'react';
import { useTranslate } from 'react-admin';
import { Box, Button, Chip, Typography } from '@mui/material';
import { MenuBookRounded as ManualIcon } from '@mui/icons-material';
import { MarkdownBody } from '@/features/manual/components/MarkdownBody';
import { screenRoute } from '../routes';
import type { ReleaseNote, ReleaseNoteKind } from '../types';
import { formatNoteDate } from '../utils';

const KIND_COLOR: Record<ReleaseNoteKind, 'warning' | 'info' | 'default'> = {
  action: 'warning',
  guests: 'info',
  feature: 'default',
};

export interface ReleaseNoteCardProps {
  note: ReleaseNote;
  /** History only: flags the notes this person has not acknowledged. */
  showNew?: boolean;
  onOpenScreen: (note: ReleaseNote) => void;
  onOpenManual: (note: ReleaseNote) => void;
}

export const ReleaseNoteCard: React.FC<ReleaseNoteCardProps> = ({ note, showNew = false, onOpenScreen, onOpenManual }) => {
  const translate = useTranslate();
  const hasScreen = screenRoute(note.screen) !== null;

  return (
    <Box
      component="article"
      sx={(theme) => ({
        minWidth: 0,
        p: 2,
        borderRadius: theme.custom.radius.medium,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
      })}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
        <Chip size="small" color={KIND_COLOR[note.kind]} label={translate(`releaseNotes.kind.${note.kind}`)} />
        {showNew && !note.seen && <Chip size="small" color="success" label={translate('releaseNotes.new')} />}
        <Typography variant="caption" color="text.secondary">
          {formatNoteDate(note.date)}
        </Typography>
      </Box>
      <Typography component="h3" variant="h4" sx={{ mb: 1, overflowWrap: 'break-word' }}>
        {note.title}
      </Typography>
      <MarkdownBody markdown={note.body} />
      {(hasScreen || note.manualChapter) && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
          {hasScreen && (
            <Button variant="outlined" size="small" onClick={() => onOpenScreen(note)}>
              {translate('releaseNotes.openScreen')}
            </Button>
          )}
          {note.manualChapter && (
            <Button variant="text" size="small" startIcon={<ManualIcon />} onClick={() => onOpenManual(note)}>
              {translate('releaseNotes.openManual')}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};
